import { useEffect, useState } from 'react'
import { ActionType } from 'klinecharts'
import type { Chart, Crosshair, KLineData } from 'klinecharts'

/**
 * O H L C (+ change, + Vol) readout over the top-left of the candle pane, in the
 * same visual grammar as the Workbench's status line. Replaces the 40px
 * "SYMBOL - TF" watermark lightweight-charts drew — klinecharts has no
 * watermark, and the symbol/timeframe are already in the config bar directly
 * above the chart.
 *
 * Follows the crosshair, falling back to the last bar. buildStyles disables the
 * engine's own OHLC tooltip (TooltipShowRule.None) so this is the only readout.
 *
 * WorkbenchStatusLine isn't reused: it requires an indicator list plus eight
 * legend callbacks that don't exist on this chart.
 */

export interface BacktestChartLegendProps {
  chart: Chart
  precision: number
  /** Bumped whenever chart data changes, so the fallback re-reads the last bar. */
  dataNonce: number
  /**
   * Forming-bar repaints during tick replay. Those are painted straight into the
   * engine without touching React state, so without this the readout would sit
   * frozen while the candle visibly grew. Re-rendering here is cheap — this
   * component is a handful of spans, and the parent chart is left alone.
   */
  subscribeRepaint?: (listener: () => void) => () => void
}

function fmt(v: number | null | undefined, p: number): string {
  return v == null ? '–' : v.toFixed(p)
}

/** Volume readout, formatted compactly (matches WorkbenchStatusLine). */
function fmtVol(v: number | undefined): string {
  if (v == null || v === 0) return 'N/A'
  if (v >= 1e9) return (v / 1e9).toFixed(2) + 'B'
  if (v >= 1e6) return (v / 1e6).toFixed(2) + 'M'
  if (v >= 1e3) return (v / 1e3).toFixed(2) + 'k'
  return String(Math.round(v))
}

export default function BacktestChartLegend({
  chart,
  precision,
  dataNonce,
  subscribeRepaint,
}: BacktestChartLegendProps) {
  const [hovered, setHovered] = useState<number | null>(null)
  const [repaintNonce, setRepaintNonce] = useState(0)

  useEffect(() => {
    if (!subscribeRepaint) return
    return subscribeRepaint(() => setRepaintNonce((n) => n + 1))
  }, [subscribeRepaint])

  useEffect(() => {
    const onCrosshair = (data?: unknown) => {
      const cross = data as Crosshair | undefined
      setHovered(cross?.dataIndex ?? null)
    }
    chart.subscribeAction(ActionType.OnCrosshairChange, onCrosshair)
    return () => {
      chart.unsubscribeAction(ActionType.OnCrosshairChange, onCrosshair)
    }
  }, [chart])

  const list = chart.getDataList() as KLineData[]
  // Both nonces are render triggers only — getDataList() above is the actual read.
  void dataNonce
  void repaintNonce
  if (list.length === 0) return null

  const idx = hovered != null && hovered >= 0 && hovered < list.length ? hovered : list.length - 1
  const bar = list[idx]
  if (!bar) return null

  const prev = idx > 0 ? list[idx - 1] : null
  const change = prev ? bar.close - prev.close : 0
  const changePct = prev && prev.close !== 0 ? (change / prev.close) * 100 : 0
  const up = change >= 0
  const priceColor = up ? 'text-candle-bull' : 'text-candle-bear'

  return (
    <div className="absolute left-2 top-1.5 z-[1] pointer-events-none select-none flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-eyebrow text-muted-foreground font-mono tabular-nums">
      <span>
        O <span className={priceColor}>{fmt(bar.open, precision)}</span>
      </span>
      <span>
        H <span className={priceColor}>{fmt(bar.high, precision)}</span>
      </span>
      <span>
        L <span className={priceColor}>{fmt(bar.low, precision)}</span>
      </span>
      <span>
        C <span className={priceColor}>{fmt(bar.close, precision)}</span>
      </span>
      {prev && (
        <span className={priceColor}>
          {up ? '+' : ''}
          {change.toFixed(precision)} ({up ? '+' : ''}
          {changePct.toFixed(2)}%)
        </span>
      )}
      <span>
        Vol <span className="text-foreground">{fmtVol(bar.volume)}</span>
      </span>
    </div>
  )
}
